import {
  Building2,
  CalendarCheck,
  Check,
  Facebook,
  Home as HomeIcon,
  MapPin,
  Phone,
  ShieldCheck,
  Sparkles,
  Star,
  UsersRound,
  Waves,
} from 'lucide-react';

const ctPhone = '203-241-4186';
const nyPhone = '845-558-4176';

const serviceIntro =
  'B&B Commercial Cleaning Services LLC provides dependable janitorial and facility cleaning for offices, multifamily properties, shared spaces, and select homes across Connecticut and New York. Since 2000, we have helped local clients keep their spaces clean, presentable, and well maintained.';

const headlineServices = [
  {
    title: 'Residential Cleaning',
    text: 'Select homes, apartments, move-ins, move-outs, and recurring care.',
    icon: HomeIcon,
  },
  {
    title: 'Multifamily Cleaning',
    text: 'Common-area cleaning for apartment buildings and shared spaces.',
    icon: UsersRound,
  },
  {
    title: 'Commercial Cleaning',
    text: 'Recurring service for offices, storefronts, and shared facilities.',
    icon: Building2,
  },
];

const coreServices = [
  {
    title: 'Office Cleaning',
    label: 'Recurring janitorial service for professional spaces',
    icon: ShieldCheck,
  },
  {
    title: 'Window Cleaning',
    label: 'Clear glass, frames, and visible high-touch areas',
    icon: Sparkles,
  },
  {
    title: 'Floor Cleaning',
    label: 'Polishing, maintenance, and careful floor care',
    icon: Waves,
  },
];

const workImages = [
  {
    src: '/images/brand/cleaning-technician.webp',
    alt: 'B&B technician with professional cleaning equipment',
  },
  {
    src: '/images/brand/20131201_182313-scaled-e1713392359916.jpg',
    alt: 'Polished commercial hallway floor',
  },
  {
    src: '/images/brand/download-1-1-e1710985245187.png',
    alt: 'Clean waiting room with polished floors',
  },
  {
    src: '/images/brand/work-1.jpg',
    alt: 'Detailed bathroom cleaning and polished marble finishes',
  },
  {
    src: '/images/brand/work-2.jpg',
    alt: 'Clean marble vanity and restroom detail work',
  },
  {
    src: '/images/brand/work-3.jpg',
    alt: 'Detailed floor cleaning in a commercial interior',
  },
];

const testimonials = [
  {
    quote:
      'Fast. Efficient. Courteous. B&B Commercial Cleaning Services LLC delivered on every front with their impeccable service and professional results. I know that I’ll be turning to B&B Commercial Cleaning Services LLC when the next opportunity arises.',
    name: 'Gary Petersen',
    role: 'CEO of Custom Carpentry',
    portrait: '/images/brand/download-3.png',
  },
  {
    quote:
      'I couldn’t be happier about my choice to work with B&B Commercial Cleaning Services LLC. They provided professional service, great pricing, and a guaranteed customer satisfaction. Thank you, B&B Commercial Cleaning Services LLC for a job well done!',
    name: 'Catherine Mollenthiel',
    role: 'Broker Owner of Dream House Realty',
    portrait: '/images/brand/download-2.png',
  },
];

function cleanPhone(phone: string) {
  return phone.replaceAll('-', '');
}

function PhoneLink({ phone, label }: { phone: string; label: string }) {
  return (
    <a className="phone-link" href={`tel:${cleanPhone(phone)}`}>
      <Phone aria-hidden="true" size={18} />
      <span>{label}</span>
    </a>
  );
}

function Stars() {
  return (
    <span aria-label="Five star rating" className="stars">
      {[0, 1, 2, 3, 4].map((star) => (
        <Star aria-hidden="true" fill="currentColor" key={star} size={18} />
      ))}
    </span>
  );
}

export default function HomePage() {
  const carouselImages = [...workImages, ...workImages];

  return (
    <main>
      <header className="topbar">
        <div className="topbar-inner">
          <div className="topbar-phones">
            <PhoneLink label={`1-${ctPhone} (CT)`} phone={ctPhone} />
            <PhoneLink label={`1-${nyPhone} (NY)`} phone={nyPhone} />
          </div>
          <div className="topbar-title">
            <strong>B&B Commercial Cleaning Services LLC</strong>
            <span>Honest. Reliable. Serving CT and NY since 2000.</span>
          </div>
        </div>
      </header>

      <nav className="site-nav" aria-label="Primary navigation">
        <a className="logo-lockup" href="#home" aria-label="B&B Commercial Cleaning home">
          <img src="/images/brand/bb-logo.png" alt="B&B Commercial Cleaning Services LLC" />
        </a>
        <div className="nav-links">
          <a href="#home">Home</a>
          <a href="#services">Services</a>
          <a href="#work">Work</a>
          <a href="#about">About</a>
          <a href="#contact">Contact</a>
        </div>
      </nav>

      <section className="hero" id="home">
        <div className="hero-inner">
          <div className="hero-panel">
            <div className="hero-copy">
              <p className="eyebrow">Commercial cleaning in Connecticut and New York</p>
              <h1>Reliable office and property cleaning in CT & NY.</h1>
              <p>{serviceIntro}</p>
              <div className="hero-actions">
                <PhoneLink label={`Call ${ctPhone} (CT)`} phone={ctPhone} />
                <PhoneLink label={`Call ${nyPhone} (NY)`} phone={nyPhone} />
              </div>
            </div>
          </div>

          <div className="hero-visual" aria-label="B&B technician at work">
            <img
              className="hero-worker"
              src="/images/brand/cleaning-technician.webp"
              alt="B&B technician with professional cleaning equipment"
            />
            <div className="hero-note">
              <strong>Since 2000</strong>
              <span>Connecticut and New York service</span>
            </div>
          </div>
        </div>

        <div className="headline-service-grid" aria-label="Primary cleaning service categories">
          {headlineServices.map((service) => {
            const Icon = service.icon;

            return (
              <article className="headline-service" key={service.title}>
                <Icon aria-hidden="true" size={30} />
                <h2>{service.title}</h2>
                <p>{service.text}</p>
              </article>
            );
          })}
        </div>
      </section>

      <section className="schedule-strip">
        <div>
          <CalendarCheck aria-hidden="true" size={24} />
          <span>Call today to schedule a walkthrough or request a free cleaning estimate.</span>
        </div>
        <a href="#contact">Request a quote</a>
      </section>

      <section className="services-section" id="services">
        <div className="section-heading centered">
          <p className="section-kicker">Our Cleaning Services</p>
          <h2>Cleaning services for offices, properties, and homes.</h2>
        </div>
        <div className="core-service-grid">
          {coreServices.map((service) => {
            const Icon = service.icon;

            return (
              <article className="core-service" key={service.title}>
                <Icon aria-hidden="true" size={38} />
                <div>
                  <h3>{service.title}</h3>
                  <p>{service.label}</p>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="work-carousel-section" id="work">
        <div className="section-heading">
          <p className="section-kicker">Examples of Our Work</p>
          <h2>Clean, maintained spaces across local properties.</h2>
        </div>
        <div className="work-carousel" aria-label="B&B cleaning work examples">
          <div className="carousel-track">
            {carouselImages.map((image, index) => (
              <figure className="carousel-card" key={`${image.src}-${index}`}>
                <img alt={image.alt} src={image.src} />
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="about-section" id="about">
        <div className="about-copy">
          <p className="section-kicker">About Us</p>
          <h2>Tried, Tested, and True</h2>
          <p>
            Since opening our doors in 2000, B&B Commercial Cleaning Services has built its reputation on consistent
            work, responsive service, and long-term relationships throughout Connecticut and New York. We clean the
            everyday spaces people notice first: offices, hallways, common areas, windows, floors, restrooms, and
            entryways.
          </p>
          <ul>
            <li>Direct phone access for Connecticut and New York service requests.</li>
            <li>Commercial, multifamily, and select residential cleaning experience.</li>
            <li>Detail-focused work for floors, windows, common areas, and high-touch spaces.</li>
          </ul>
        </div>
        <figure className="about-photo">
          <img
            src="/images/brand/20131201_182313-scaled-e1713392359916.jpg"
            alt="Polished commercial hallway maintained by B&B"
          />
        </figure>
      </section>

      <section className="testimonial-section">
        <div className="section-heading centered compact">
          <p className="section-kicker">Testimonials</p>
          <h2>Clients choose B&B for reliable service and professional results.</h2>
        </div>
        <div className="testimonial-grid">
          {testimonials.map((testimonial) => (
            <figure className="testimonial" key={testimonial.name}>
              <img src={testimonial.portrait} alt={testimonial.name} />
              <blockquote>
                <span>{testimonial.quote}</span>
              </blockquote>
              <figcaption>
                <strong>{testimonial.name}</strong>
                <span>{testimonial.role}</span>
                <Stars />
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="cta-section" id="contact">
        <div className="cta-inner">
          <p className="section-kicker">Schedule a walkthrough</p>
          <h2>Request a Free Cleaning Estimate</h2>
          <p>
            Tell us about your office, building, shared space, or home. Call {ctPhone} (CT) or {nyPhone} (NY) to
            request a quote and ask about our 10% discount for new accounts.
          </p>
          <div className="hero-actions">
            <PhoneLink label={`Call ${ctPhone} (CT)`} phone={ctPhone} />
            <PhoneLink label={`Call ${nyPhone} (NY)`} phone={nyPhone} />
          </div>
        </div>
      </section>

      <footer className="site-footer" id="locations">
        <div className="footer-inner">
          <div>
            <h2>Connecticut (CT)</h2>
            <a href={`tel:${cleanPhone(ctPhone)}`}>{ctPhone}</a>
            <h2>New York State (NY)</h2>
            <a href={`tel:${cleanPhone(nyPhone)}`}>{nyPhone}</a>
            <div className="social-links">
              <a href="https://www.facebook.com/bbcommercialcleaning/" rel="noreferrer" target="_blank">
                <Facebook aria-hidden="true" size={18} />
                <span>Facebook</span>
              </a>
              <a href="https://share.google/hHzcSwdg4knlgQyBa" rel="noreferrer" target="_blank">
                <MapPin aria-hidden="true" size={18} />
                <span>Google</span>
              </a>
            </div>
          </div>

          <div>
            <h2>Services</h2>
            {coreServices.map((service) => (
              <a href="#services" key={service.title}>
                <Check aria-hidden="true" size={17} />
                <span>{service.title}</span>
              </a>
            ))}
          </div>

          <img
            className="footer-card"
            src="/images/brand/business-card.jpg"
            alt="B&B Commercial Cleaning business card"
          />
        </div>
        <p className="footer-bottom">© 2026 B&B Commercial Cleaning. All rights reserved.</p>
      </footer>
    </main>
  );
}
